import React from "react";
import { Link } from "react-router-dom";
import classes from "./record.module.scss";

export interface AttentionItem {
    key: string;
    label: React.ReactNode;
    /** warning (default) or danger; info for a heads-up. */
    tone?: "info" | "warning" | "danger";
    /** Hover text — what is wrong and what clicking fixes. */
    tooltip?: string;
    /** Bootstrap Icons name without the `bi-` prefix. */
    icon?: string;
    /** In-app route to the fix. */
    to?: string;
    /** Opens the drawer / field that fixes it. */
    onClick?: () => void;
}

interface AttentionBarProps {
    items: AttentionItem[];
    title?: string;
}

const toneClass = (tone: AttentionItem["tone"]): string =>
    tone === "danger" ? classes.attentionDanger : tone === "info" ? classes.attentionInfo : classes.attentionWarning;

/**
 * What needs fixing on this record (expiring documents, overdue tasks,
 * missing basics), each chip deep-linking to the fix. Renders nothing when
 * nothing needs attention.
 */
const AttentionBar: React.FC<AttentionBarProps> = ({ items, title = "Needs attention" }) => {
    if (items.length === 0) return null;
    return (
        <section className={classes.attention} aria-label={title}>
            <span className={classes.attentionTitle}>
                <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                {title}
            </span>
            {items.map((item) => {
                const itemClass = `${classes.attentionItem} ${toneClass(item.tone)}`;
                const content = (
                    <>
                        {item.icon && <i className={`bi bi-${item.icon}`} aria-hidden="true" />}
                        {item.label}
                    </>
                );
                if (item.to) {
                    return <Link key={item.key} className={itemClass} to={item.to} title={item.tooltip}>{content}</Link>;
                }
                if (item.onClick) {
                    return (
                        <button key={item.key} type="button" className={`btn-unstyled ${itemClass}`} title={item.tooltip} onClick={item.onClick}>
                            {content}
                        </button>
                    );
                }
                return <span key={item.key} className={itemClass} title={item.tooltip}>{content}</span>;
            })}
        </section>
    );
};

export default AttentionBar;
